import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { dictionaryApi, educationalApi, referenceApi } from '../../api/endpoints';
import {
    Checkbox,
    FileField,
    FormActions,
    FormSection,
    MultiSelect,
    TextArea,
    TextInput,
} from '../../components/Form';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const EMPTY = {
    name: '',
    description: '',
    annotations: '',
    publishers: '',
    pages: '',
    date: '',
    categories: [],
    departments: [],
    authors: [],
    hide: false,
};

const EducationalForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const { toast } = useUi();
    const isEdit = Boolean(id);

    const [values, setValues] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [image, setImage] = useState(null);
    const [current, setCurrent] = useState({ file: null, image: null });

    const [authorOptions, setAuthorOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [errors, setErrors] = useState({});
    const [loadError, setLoadError] = useState(null);

    const set = useCallback((key, value) => {
        setValues((v) => ({ ...v, [key]: value }));
        setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    }, []);

    useEffect(() => {
        let cancelled = false;

        Promise.all([
            dictionaryApi.options('ed-authors'),
            dictionaryApi.options('ed-categories'),
            referenceApi.departments(),
        ])
            .then(([authors, categories, departments]) => {
                if (cancelled) return;
                setAuthorOptions(authors.map((a) => ({ value: a.id, label: a.name })));
                setCategoryOptions(categories.map((c) => ({ value: c.id, label: c.name })));
                setDepartmentOptions((departments || []).map((d) => ({ value: d.id, label: d.name })));
            })
            .catch((e) => !cancelled && toast.error(`Не удалось загрузить справочники: ${e.message}`));

        return () => {
            cancelled = true;
        };
    }, [toast]);

    useEffect(() => {
        if (!isEdit) return undefined;

        let cancelled = false;
        setLoading(true);

        educationalApi
            .get(id)
            .then((item) => {
                if (cancelled) return;
                setValues({
                    name: item.name || '',
                    description: item.description || '',
                    annotations: item.annotations || '',
                    publishers: item.publishers || '',
                    pages: item.pages || '',
                    date: item.date || '',
                    // Прежняя форма редактирования открывала эти списки пустыми.
                    categories: item.categories.map((c) => c.id),
                    departments: item.departments.map((d) => d.id),
                    authors: item.authors.map((a) => a.id),
                    hide: item.hide === 1,
                });
                setCurrent({ file: item.file || null, image: item.image || null });
            })
            .catch((e) => !cancelled && setLoadError(e))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSaving(true);
        setProgress(0);

        const payload = {
            name: values.name,
            description: values.description,
            annotations: values.annotations,
            publishers: values.publishers,
            pages: values.pages,
            date: values.date,
            categories: values.categories,
            departments: values.departments,
            authors: values.authors,
            hide: values.hide,
        };
        if (file) payload.file = file;
        if (image) payload.image = image;

        try {
            if (isEdit) {
                await educationalApi.update(id, payload, setProgress);
                toast.success('Изменения сохранены');
                setFile(null);
                setImage(null);
                const fresh = await educationalApi.get(id);
                setCurrent({ file: fresh.file || null, image: fresh.image || null });
            } else {
                const created = await educationalApi.create(payload, setProgress);
                toast.success('Издание добавлено');
                history.push(`/admin/educational/${created.id}`);
            }
        } catch (err) {
            if (err.isValidation) {
                setErrors(err.errors || {});
                toast.error('Проверьте заполнение полей');
            } else {
                toast.error(err.message);
            }
        } finally {
            setSaving(false);
            setProgress(0);
        }
    };

    if (loadError) {
        return (
            <div className="adm-card adm-card-body">
                <p>Не удалось загрузить издание: {loadError.message}</p>
                <NavLink to="/admin/educational" className="adm-btn adm-btn-secondary">
                    К списку изданий
                </NavLink>
            </div>
        );
    }

    return (
        <div className={styles.formPage}>
            <NavLink to="/admin/educational" className={styles.backLink}>
                ← К списку изданий
            </NavLink>

            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">
                        {isEdit ? 'Редактирование издания' : 'Новое учебное издание'}
                    </h1>
                    {isEdit && <p className="adm-page-subtitle">{values.name}</p>}
                </div>
            </div>

            {loading ? (
                <div className="adm-card">
                    <div className={styles.formLoading}>Загрузка данных издания…</div>
                </div>
            ) : (
                <form className="adm-card" onSubmit={handleSubmit}>
                    <FormSection title="Основное">
                        <TextInput
                            id="ed-name"
                            label="Название"
                            required
                            value={values.name}
                            onChange={(e) => set('name', e.target.value)}
                            error={errors.name && errors.name[0]}
                        />

                        <TextArea
                            id="ed-annotations"
                            label="Аннотация"
                            value={values.annotations}
                            onChange={(e) => set('annotations', e.target.value)}
                            error={errors.annotations && errors.annotations[0]}
                        />

                        <TextArea
                            id="ed-description"
                            label="Описание"
                            value={values.description}
                            onChange={(e) => set('description', e.target.value)}
                            error={errors.description && errors.description[0]}
                        />

                        <div className="adm-grid-2">
                            <TextInput
                                id="ed-publishers"
                                label="Издательство"
                                value={values.publishers}
                                onChange={(e) => set('publishers', e.target.value)}
                                error={errors.publishers && errors.publishers[0]}
                            />
                            <TextInput
                                id="ed-pages"
                                label="Количество страниц"
                                value={values.pages}
                                onChange={(e) => set('pages', e.target.value)}
                                error={errors.pages && errors.pages[0]}
                            />
                        </div>

                        <TextInput
                            id="ed-date"
                            type="date"
                            label="Дата публикации"
                            value={values.date}
                            onChange={(e) => set('date', e.target.value)}
                            error={errors.date && errors.date[0]}
                        />
                    </FormSection>

                    <FormSection title="Связи">
                        <MultiSelect
                            label="Авторы"
                            options={authorOptions}
                            value={values.authors}
                            onChange={(v) => set('authors', v)}
                        />
                        <MultiSelect
                            label="Направления"
                            options={categoryOptions}
                            value={values.categories}
                            onChange={(v) => set('categories', v)}
                        />
                        <MultiSelect
                            label="Кафедры"
                            options={departmentOptions}
                            value={values.departments}
                            onChange={(v) => set('departments', v)}
                        />
                    </FormSection>

                    <FormSection title="Файлы и доступ">
                        <FileField
                            id="ed-file"
                            label="Документ (PDF)"
                            accept="application/pdf"
                            currentPath={current.file}
                            file={file}
                            onChange={setFile}
                            progress={progress}
                            error={errors.file && errors.file[0]}
                            hint={isEdit ? 'Если файл не выбран, текущий останется без изменений.' : undefined}
                        />

                        <FileField
                            id="ed-image"
                            label="Обложка"
                            accept="image/*"
                            currentLabel="Текущая обложка"
                            currentPath={current.image}
                            file={image}
                            onChange={setImage}
                            error={errors.image && errors.image[0]}
                        />

                        <Checkbox
                            id="ed-hide"
                            label="Открытый доступ"
                            checked={values.hide}
                            onChange={(v) => set('hide', v)}
                            hint="Включено — на сайте показывается прямая ссылка на файл. Выключено — только просмотр во встроенной читалке."
                        />
                    </FormSection>

                    <FormActions>
                        <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
                            {saving && <span className="adm-spinner" />}
                            {saving ? 'Сохранение…' : isEdit ? 'Сохранить изменения' : 'Добавить издание'}
                        </button>
                        <NavLink to="/admin/educational" className="adm-btn adm-btn-secondary">
                            Отмена
                        </NavLink>
                        {saving && progress > 0 && progress < 100 && (
                            <span className="adm-muted">Загрузка файла: {progress}%</span>
                        )}
                    </FormActions>
                </form>
            )}
        </div>
    );
};

export default EducationalForm;
